import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ec from "@/i18n/ec.json";
import en from "@/i18n/en.json";

const DICTS = { ec, en };
const SUPPORTED = ["ec", "en"];

const I18nContext = createContext(null);

function getByPath(obj, path) {
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const langFromPath = useMemo(() => {
    const seg = location.pathname.split("/")[1];
    return SUPPORTED.includes(seg) ? seg : "ec";
  }, [location.pathname]);

  const [lang, setLang] = useState(langFromPath);

  useEffect(() => {
    if (langFromPath !== lang) setLang(langFromPath);
  }, [langFromPath, lang]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang === "ec" ? "es-EC" : "en";
    }
  }, [lang]);

  const t = useMemo(() => {
    const dict = DICTS[lang] || DICTS.ec;
    return (key, fallback = "") => {
      const val = getByPath(dict, key);
      if (val === undefined) return fallback || key;
      return val;
    };
  }, [lang]);

  const switchLanguage = (nextLang) => {
    if (!SUPPORTED.includes(nextLang) || nextLang === lang) return;
    const parts = location.pathname.split("/");
    if (SUPPORTED.includes(parts[1])) {
      parts[1] = nextLang;
    } else {
      parts.splice(1, 0, nextLang);
    }
    const nextPath = parts.join("/") || `/${nextLang}`;
    navigate(nextPath + location.search + location.hash, { replace: false, state: { languageSwitch: true } });
  };

  const buildPath = (subpath = "") => {
    const clean = subpath.replace(/^\/+/, "");
    return clean ? `/${lang}/${clean}` : `/${lang}`;
  };

  const value = { lang, t, switchLanguage, buildPath };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
